import { cachedFetch, invalidateCache } from './cache';
import { supabase } from '../lib/supabaseClient';
import type {
  AttendanceCorrection,
  AttendanceEvent,
  AttendancePolicy,
  DisciplinaryAction,
  Division,
  EffectiveAttendanceEvent,
  EffectiveTardiness,
  Employee,
  LeaveRequest,
  Schedule,
  Shift,
  ShiftSwap,
  Tardiness,
  UUID,
} from '../types/domain';

/**
 * HR (FASE 8/9) — real reads wired to the app, gated by the real
 * employee/attendance_event/leave_request/shift_swap/disciplinary_action
 * permissions (migrations 0035/0036/0040/0041). `employees` reads are
 * scoped to the roster only — no compensation data (payroll_lines/
 * payroll_facts) is exposed by any adapter yet; that needs its own read
 * path once a UI actually needs per-employee salary figures, not just
 * this dashboard's aggregate counts. Every write below calls a
 * permission-gated RPC (migration 0040) — never a direct
 * `.insert()`/`.update()`.
 */

// ---------------------------------------------------------------------------
// Employees
// ---------------------------------------------------------------------------

export async function listEmployees(branchId?: string): Promise<Employee[]> {
  let query = supabase
    .from('employees')
    .select('employee_id, tenant_id, employee_no, legal_name, branch_id, division_id, status, created_at')
    .order('employee_no');
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listEmployees failed: ${error.message}`);
  return (data ?? []) as Employee[];
}

/** Tenant-wide reference data (no branch_id on divisions) — cached like uom/branches. */
export async function listDivisions(): Promise<Division[]> {
  return cachedFetch('divisions', async () => {
    const { data, error } = await supabase.from('divisions').select('division_id, tenant_id, name, status').order('name');
    if (error) throw new Error(`listDivisions failed: ${error.message}`);
    return (data ?? []) as Division[];
  });
}

/** Calls fn_create_employee (migration 0040, permission-gated on employee.create, branch-scoped). */
export async function createEmployee(employeeNo: string, legalName: string, branchId: UUID, divisionId?: UUID | null): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_employee', {
    p_employee_no: employeeNo,
    p_legal_name: legalName,
    p_branch_id: branchId,
    p_division_id: divisionId ?? null,
    p_status: 'ACTIVE',
  });
  if (error) throw new Error(`fn_create_employee failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_update_employee (migration 0040, permission-gated on employee.create — the same action covers create/edit, matching item.create's convention). */
export async function updateEmployee(
  employeeId: UUID,
  legalName: string,
  branchId: UUID,
  divisionId: UUID | null,
  status: Employee['status']
): Promise<void> {
  const { error } = await supabase.rpc('fn_update_employee', {
    p_employee_id: employeeId,
    p_legal_name: legalName,
    p_branch_id: branchId,
    p_division_id: divisionId,
    p_status: status,
  });
  if (error) throw new Error(`fn_update_employee failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Attendance
// ---------------------------------------------------------------------------

export async function listAttendanceEventsToday(branchId?: string): Promise<AttendanceEvent[]> {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  let query = supabase
    .from('attendance_events')
    .select('event_id, tenant_id, branch_id, employee_id, schedule_id, event_type, event_time')
    .gte('event_time', startOfDay.toISOString())
    .order('event_time', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listAttendanceEventsToday failed: ${error.message}`);
  return (data ?? []) as AttendanceEvent[];
}

/** Real operational range read for the Attendance workspace — no invented records, only what fn_record_attendance_event actually wrote. */
export async function listAttendanceEvents(branchId?: string, fromISO?: string, toISO?: string): Promise<AttendanceEvent[]> {
  let query = supabase
    .from('attendance_events')
    .select('event_id, tenant_id, branch_id, employee_id, schedule_id, event_type, event_time')
    .order('event_time', { ascending: false })
    .limit(500);
  if (branchId) query = query.eq('branch_id', branchId);
  if (fromISO) query = query.gte('event_time', fromISO);
  if (toISO) query = query.lte('event_time', toISO);
  const { data, error } = await query;
  if (error) throw new Error(`listAttendanceEvents failed: ${error.message}`);
  return (data ?? []) as AttendanceEvent[];
}

/** Real, deterministic lateness only — never fabricated. Empty when no attendance_policies row covers the clock-in. */
export async function listTardiness(branchId?: string): Promise<Tardiness[]> {
  let query = supabase
    .from('tardiness')
    .select('tardiness_id, tenant_id, branch_id, employee_id, schedule_id, attendance_event_id, policy_id, late_minutes, created_at')
    .order('created_at', { ascending: false })
    .limit(500);
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listTardiness failed: ${error.message}`);
  return (data ?? []) as Tardiness[];
}

/** PLANNED schedules for one employee — the only source of valid p_schedule_id values for recording attendance or requesting a swap (no schedule-authoring UI in this slice). */
export async function listPlannedSchedules(employeeId: UUID): Promise<Schedule[]> {
  const { data, error } = await supabase
    .from('schedules')
    .select('schedule_id, tenant_id, branch_id, employee_id, shift_id, work_date, status')
    .eq('employee_id', employeeId)
    .eq('status', 'PLANNED')
    .order('work_date', { ascending: false })
    .limit(30);
  if (error) throw new Error(`listPlannedSchedules failed: ${error.message}`);
  return (data ?? []) as Schedule[];
}

export async function listAttendancePolicies(branchId?: string | null): Promise<AttendancePolicy[]> {
  let query = supabase
    .from('attendance_policies')
    .select('policy_id, tenant_id, branch_id, version_no, status, effective_from, effective_to, late_tolerance_minutes')
    .order('effective_from', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listAttendancePolicies failed: ${error.message}`);
  return (data ?? []) as AttendancePolicy[];
}

/** Calls fn_record_attendance_event_checked (migration 0040, permission-gated on attendance_event.create, branch-scoped). Automatically computes tardiness server-side — this adapter never derives lateness itself. */
export async function recordAttendanceEvent(scheduleId: UUID, employeeId: UUID, eventType: 'CLOCK_IN' | 'CLOCK_OUT'): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_record_attendance_event_checked', {
    p_schedule_id: scheduleId,
    p_employee_id: employeeId,
    p_event_type: eventType,
  });
  if (error) throw new Error(`fn_record_attendance_event_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_create_attendance_policy_version_checked (migration 0040, permission-gated on attendance_policy.create). branchId null = tenant-wide policy. */
export async function createAttendancePolicyVersion(branchId: UUID | null, effectiveFrom: string, lateToleranceMinutes: number): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_attendance_policy_version_checked', {
    p_branch_id: branchId,
    p_effective_from: effectiveFrom,
    p_late_tolerance_minutes: lateToleranceMinutes,
  });
  if (error) throw new Error(`fn_create_attendance_policy_version_checked failed: ${error.message}`);
  invalidateCache('attendance_policies');
  return data as UUID;
}

/**
 * Calls fn_correct_attendance_event_checked (migration 0043, permission-
 * gated on attendance_event.correct). The original attendance_events row
 * stays immutable (trg_attendance_events_no_update) — this inserts a
 * canonical attendance_corrections row (original + corrected value snapshot,
 * reason, actor) and automatically supersedes any prior APPLIED correction
 * on the same event, so exactly one correction is ever effective at a time.
 */
export async function correctAttendanceEvent(eventId: UUID, correctedEventTimeISO: string, reason: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_correct_attendance_event_checked', {
    p_event_id: eventId,
    p_corrected_event_time: correctedEventTimeISO,
    p_reason: reason,
  });
  if (error) throw new Error(`fn_correct_attendance_event_checked failed: ${error.message}`);
  return data as UUID;
}

/** Full correction history (APPLIED + SUPERSEDED) for one attendance event, newest first — for the Koreksi drawer's audit trail. */
export async function listAttendanceCorrections(eventId: UUID): Promise<AttendanceCorrection[]> {
  const { data, error } = await supabase
    .from('attendance_corrections')
    .select('correction_id, tenant_id, branch_id, attendance_event_id, employee_id, original_event_time, corrected_event_time, reason, status, requested_by, created_at')
    .eq('attendance_event_id', eventId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listAttendanceCorrections failed: ${error.message}`);
  return (data ?? []) as AttendanceCorrection[];
}

/** RAW + latest APPLIED correction = EFFECTIVE (fn_get_effective_attendance_event, migration 0043). Pure read-time projection, never a stored row. */
export async function getEffectiveAttendanceEvent(eventId: UUID): Promise<EffectiveAttendanceEvent | null> {
  const { data, error } = await supabase.rpc('fn_get_effective_attendance_event', { p_event_id: eventId });
  if (error) throw new Error(`fn_get_effective_attendance_event failed: ${error.message}`);
  const rows = (data ?? []) as EffectiveAttendanceEvent[];
  return rows[0] ?? null;
}

/** Lateness recomputed from the EFFECTIVE (post-correction) event time, using the exact same policy-resolution formula as fn_record_attendance_event — the historical `tardiness` row is never rewritten by this. */
export async function getEffectiveTardiness(eventId: UUID): Promise<EffectiveTardiness | null> {
  const { data, error } = await supabase.rpc('fn_get_effective_tardiness', { p_event_id: eventId });
  if (error) throw new Error(`fn_get_effective_tardiness failed: ${error.message}`);
  const rows = (data ?? []) as EffectiveTardiness[];
  return rows[0] ?? null;
}

// ---------------------------------------------------------------------------
// Shift Master (migration 0042)
// ---------------------------------------------------------------------------

export async function listShifts(branchId?: string): Promise<Shift[]> {
  let query = supabase
    .from('shifts')
    .select('shift_id, tenant_id, branch_id, shift_code, name, start_time, end_time, crosses_midnight, status')
    .order('shift_code');
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listShifts failed: ${error.message}`);
  return (data ?? []) as Shift[];
}

/** Calls fn_create_shift_checked (migration 0042, permission-gated on shift.create). crosses_midnight is derived server-side from start/end time — never sent by the caller. */
export async function createShift(branchId: UUID, shiftCode: string, name: string, startTime: string, endTime: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_shift_checked', {
    p_branch_id: branchId,
    p_shift_code: shiftCode,
    p_name: name,
    p_start_time: startTime,
    p_end_time: endTime,
  });
  if (error) throw new Error(`fn_create_shift_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_update_shift_checked (migration 0042, permission-gated on shift.create — same action as create, matching the create/update pairing already used for employees). */
export async function updateShift(shiftId: UUID, name: string, startTime: string, endTime: string, status: Shift['status']): Promise<void> {
  const { error } = await supabase.rpc('fn_update_shift_checked', {
    p_shift_id: shiftId,
    p_name: name,
    p_start_time: startTime,
    p_end_time: endTime,
    p_status: status,
  });
  if (error) throw new Error(`fn_update_shift_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Schedule authoring (migration 0042)
// ---------------------------------------------------------------------------

/** Broader than listPlannedSchedules — every status, date-range/branch/employee filterable, for the Schedule Calendar workspace. */
export async function listSchedules(branchId?: string, fromISO?: string, toISO?: string, employeeId?: UUID): Promise<Schedule[]> {
  let query = supabase
    .from('schedules')
    .select('schedule_id, tenant_id, branch_id, employee_id, shift_id, work_date, status')
    .order('work_date', { ascending: true })
    .limit(1000);
  if (branchId) query = query.eq('branch_id', branchId);
  if (fromISO) query = query.gte('work_date', fromISO);
  if (toISO) query = query.lte('work_date', toISO);
  if (employeeId) query = query.eq('employee_id', employeeId);
  const { data, error } = await query;
  if (error) throw new Error(`listSchedules failed: ${error.message}`);
  return (data ?? []) as Schedule[];
}

/** Calls fn_create_schedule_checked (migration 0042, permission-gated on schedule.create, branch derived from the employee). */
export async function createSchedule(employeeId: UUID, shiftId: UUID, workDate: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_schedule_checked', {
    p_employee_id: employeeId,
    p_shift_id: shiftId,
    p_work_date: workDate,
  });
  if (error) throw new Error(`fn_create_schedule_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_reassign_schedule_checked (migration 0042, permission-gated on schedule.update). Only allowed while PLANNED and with no recorded attendance yet. */
export async function reassignSchedule(scheduleId: UUID, newEmployeeId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_reassign_schedule_checked', {
    p_schedule_id: scheduleId,
    p_new_employee_id: newEmployeeId,
  });
  if (error) throw new Error(`fn_reassign_schedule_checked failed: ${error.message}`);
}

/** Calls fn_cancel_schedule_checked (migration 0042, permission-gated on schedule.update). Only allowed while PLANNED and with no recorded attendance yet. */
export async function cancelSchedule(scheduleId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_cancel_schedule_checked', { p_schedule_id: scheduleId });
  if (error) throw new Error(`fn_cancel_schedule_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Leave requests
// ---------------------------------------------------------------------------

export async function listLeaveRequests(branchId?: string): Promise<LeaveRequest[]> {
  let query = supabase
    .from('leave_requests')
    .select('leave_id, tenant_id, branch_id, employee_id, leave_type, date_from, date_to, status, reason, requested_by, decided_by, decided_at, created_at')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listLeaveRequests failed: ${error.message}`);
  return (data ?? []) as LeaveRequest[];
}

/** Calls fn_create_leave_request (migration 0040, permission-gated on leave_request.create, branch derived from the employee). */
export async function createLeaveRequest(
  employeeId: UUID,
  leaveType: LeaveRequest['leave_type'],
  dateFrom: string,
  dateTo: string,
  reason?: string | null
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_leave_request', {
    p_employee_id: employeeId,
    p_leave_type: leaveType,
    p_date_from: dateFrom,
    p_date_to: dateTo,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(`fn_create_leave_request failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_decide_leave_request_checked (migration 0040, permission-gated on leave_request.approve, branch-scoped). */
export async function decideLeaveRequest(leaveId: UUID, approve: boolean): Promise<void> {
  const { error } = await supabase.rpc('fn_decide_leave_request_checked', { p_leave_id: leaveId, p_approve: approve });
  if (error) throw new Error(`fn_decide_leave_request_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Shift swaps
// ---------------------------------------------------------------------------

export async function listShiftSwaps(branchId?: string): Promise<ShiftSwap[]> {
  let query = supabase
    .from('shift_swaps')
    .select('swap_id, tenant_id, branch_id, schedule_id, requesting_employee_id, covering_employee_id, status, reason, requested_by, decided_by, decided_at, created_at')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listShiftSwaps failed: ${error.message}`);
  return (data ?? []) as ShiftSwap[];
}

/** Calls fn_create_shift_swap (migration 0040, permission-gated on shift_swap.create, branch derived from the schedule). The requesting employee is always the schedule's current assignee, resolved server-side. */
export async function createShiftSwap(scheduleId: UUID, coveringEmployeeId: UUID, reason?: string | null): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_shift_swap', {
    p_schedule_id: scheduleId,
    p_covering_employee_id: coveringEmployeeId,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(`fn_create_shift_swap failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_decide_shift_swap_checked (migration 0040, permission-gated on shift_swap.approve, branch-scoped). Approving reassigns the underlying schedule to the covering employee server-side. */
export async function decideShiftSwap(swapId: UUID, approve: boolean): Promise<void> {
  const { error } = await supabase.rpc('fn_decide_shift_swap_checked', { p_swap_id: swapId, p_approve: approve });
  if (error) throw new Error(`fn_decide_shift_swap_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Disciplinary actions — sensitive: HR + TENANT_OWNER only, enforced by a
// role-restrictive RLS policy (migration 0041), not just hidden UI.
// ---------------------------------------------------------------------------

export async function listDisciplinaryActions(branchId?: string): Promise<DisciplinaryAction[]> {
  let query = supabase
    .from('disciplinary_actions')
    .select('action_id, tenant_id, branch_id, employee_id, action_type, reason, related_tardiness_id, status, issued_by, issued_at, created_at')
    .order('issued_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listDisciplinaryActions failed: ${error.message}`);
  return (data ?? []) as DisciplinaryAction[];
}

/** Calls fn_issue_disciplinary_action_checked (migration 0040, permission-gated on disciplinary_action.create, branch derived from the employee). */
export async function issueDisciplinaryAction(
  employeeId: UUID,
  actionType: DisciplinaryAction['action_type'],
  reason: string,
  relatedTardinessId?: UUID | null
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_issue_disciplinary_action_checked', {
    p_employee_id: employeeId,
    p_action_type: actionType,
    p_reason: reason,
    p_related_tardiness_id: relatedTardinessId ?? null,
  });
  if (error) throw new Error(`fn_issue_disciplinary_action_checked failed: ${error.message}`);
  return data as UUID;
}
